import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { adminService } from '../../../services/subscriptionService';
import { AdminUser } from '../../../types';

interface Props {
  user: AdminUser;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const BanUserDialog = ({ user, open, onClose, onSuccess }: Props) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      adminService.banUser(user.id, reason, expiresAt || undefined),
    onSuccess,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle color="error">{t('admin.banDialog.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('admin.banDialog.subtitle', { name: `${user.first_name} ${user.last_name}` })}
        </Typography>
        <TextField
          label={t('admin.banDialog.reason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          required
          sx={{ mb: 2 }}
        />
        <TextField
          label={t('admin.banDialog.expiresAt')}
          type="datetime-local"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          fullWidth
          helperText={t('admin.banDialog.expiresAtHelp')}
          InputLabelProps={{ shrink: true }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || !reason.trim()}
          startIcon={mutation.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {t('admin.banDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
